// src/supplier/layout/SupplierLayout.jsx
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import Swal, { getApparifyLogoutDialogOptions } from "../../utils/apparifySwal";
import { useSupplierAuth } from "../context/SupplierAuthContext";
import { supplierAuthAPI } from "../services/supplierApi";
import { Menu as MenuIcon } from "lucide-react";
import SupplierTopbar from "../components/SupplierTopbar";
import SupplierSidebar from "../components/SupplierSidebar";
import { IconButton, Sheet } from "../../components/ui";
import "../../styles/operations-portal.css";
import "../../business/business-ui.css";
import "../supplier-ui.css";

export default function SupplierLayout() {
  const { user, suppliers, logout } = useSupplierAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const loggingOutRef = useRef(false);
  const contentRef = useRef(null);

  const scrollSupplierContentToTop = useCallback(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    const pageScroller = document.scrollingElement || document.documentElement;
    if (pageScroller) pageScroller.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, []);

  const handleSidebarNavigate = useCallback(() => {
    scrollSupplierContentToTop();
    setMobileNavOpen(false);
  }, [scrollSupplierContentToTop]);

  const handleLogout = async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      const result = await Swal.fire({
        ...getApparifyLogoutDialogOptions("supplier"),
      });
      if (!result.isConfirmed) return;

      await supplierAuthAPI.logout();
      await logout();
      // Use the body-hosted shared dialog so the auth guard cannot unmount it.
      await Swal.fire({
        icon: "success",
        title: "Signed out successfully",
        html: "Redirecting you to the supplier login page...",
        timer: 900,
        showConfirmButton: false,
      });
      navigate("/supplier/login", { replace: true });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Logout failed",
        text: error?.message || "Unable to logout. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      loggingOutRef.current = false;
    }
  };

  useEffect(() => {
    document.body.classList.add("supplier-portal-active");
    return () => document.body.classList.remove("supplier-portal-active");
  }, []);

  useEffect(() => {
    const id = "supplier-swal-root";
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement("div");
      node.id = id;
      document.body.appendChild(node);
    }
    return () => {};
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    scrollSupplierContentToTop();
  }, [pathname, scrollSupplierContentToTop]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleChange = (event) => {
      if (event.matches) setMobileNavOpen(false);
    };

    if (mediaQuery.matches) setMobileNavOpen(false);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <div className="supplier-portal min-h-screen bg-canvas text-foreground md:h-screen md:overflow-hidden">
      <div className="supplier-app-frame mx-auto min-h-screen w-full md:h-full">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen} title="Supplier navigation" titleHidden>
          <SupplierSidebar onNavigate={handleSidebarNavigate} onClose={() => setMobileNavOpen(false)} />
        </Sheet>

        <div className="flex min-h-screen w-full md:h-full md:min-h-0">
          <aside className="supplier-sidebar-shell hidden h-full min-h-0 w-[var(--layout-sidebar)] shrink-0 overflow-hidden border-r border-default bg-surface md:block">
            <SupplierSidebar onNavigate={handleSidebarNavigate} />
          </aside>
          <div className="flex min-w-0 flex-1 flex-col md:h-full md:min-h-0">
            <header className="supplier-topbar-shell sticky top-0 z-30 h-[var(--layout-topbar)] shrink-0 border-b border-default bg-surface">
              <div className="flex h-full items-center gap-2 px-2 md:px-6">
                <IconButton icon={MenuIcon} label="Open menu" onClick={() => setMobileNavOpen(true)} className="md:hidden" />
                <div className="h-full min-w-0 flex-1">
                  <SupplierTopbar user={user} suppliers={suppliers} onLogout={handleLogout} />
                </div>
              </div>
            </header>
            <main ref={contentRef} className="supplier-content min-h-0 min-w-0 flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-fade md:px-6 md:py-6">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
      <div id="supplier-swal-root" />
    </div>
  );
}
